import { createContext, useContext, useMemo, useState, type ReactNode } from 'react'
import type { SessionUser, UserRole } from '../data/app-adapter'
import { MOCK_USER } from '../data/mock-adapter'

interface SessionContextValue {
  user: SessionUser | null
  isPreview: boolean
  signInPreview(): void
  signOut(): void
  setPreviewRole(role: UserRole): void
}

const SessionContext = createContext<SessionContextValue | null>(null)

const roleLabels: Record<UserRole, string> = {
  employee: '正職一級',
  supervisor: '帶教主管',
  manager_l1: '店長一級',
  manager_l2: '店長二級',
  area_supervisor: '督導',
}

function initialPreviewUser(): SessionUser {
  const requestedRole = new URLSearchParams(window.location.search).get('previewRole') as UserRole | null
  if (!requestedRole || !(requestedRole in roleLabels)) return MOCK_USER
  return { ...MOCK_USER, role: requestedRole, roleLabel: roleLabels[requestedRole] }
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<SessionUser | null>(initialPreviewUser)

  const value = useMemo<SessionContextValue>(() => ({
    user,
    isPreview: true,
    signInPreview: () => setUser(MOCK_USER),
    signOut: () => setUser(null),
    setPreviewRole: (role) => setUser((current) => ({
      ...(current ?? MOCK_USER),
      role,
      roleLabel: roleLabels[role],
    })),
  }), [user])

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession() {
  const context = useContext(SessionContext)
  if (!context) throw new Error('useSession must be used within SessionProvider')
  return context
}
